import React, { useCallback } from 'react';
import {
  VirtualizedMeasurement,
  measureVirtualizedNodes,
} from './measure-virtualized-nodes';
import { VirtualizedState } from './virtualized-state';

const computeVerticalSlice = (
  scrollable: HTMLElement,
  state: VirtualizedState,
): { start: number; end: number } => {
  const { children, childSizes, meanChildSize, overscan } = state;

  const { offsetHeight, scrollTop } = scrollable;
  const topThreshold = scrollTop - overscan;
  const bottomThreshold = scrollTop + offsetHeight + overscan;

  let start = 0;
  let end = 0;
  let accumulator = 0;

  for (let i = 0; i < children.length; i++) {
    const curr = childSizes[i];
    const childHeight = curr == null ? meanChildSize : curr;
    if (accumulator <= topThreshold) {
      start++;
    }
    if (accumulator <= bottomThreshold) {
      end++;
    }
    accumulator += childHeight;
  }

  // Update the slice ref.
  return {
    end: end + state.batchSize,
    start: Math.max(0, start - state.batchSize),
  };
};

const shouldUpdateSlice = (
  state: VirtualizedState,
  direction: 'vertical' | 'horizontal',
  scrollable: HTMLElement,
  measurements: VirtualizedMeasurement[],
): boolean => {
  const first = measurements[0];
  const last = measurements[measurements.length - 1];

  if (
    last &&
    state.currentSlice.end < state.children.length &&
    scrollable.scrollTop + scrollable.offsetHeight + state.threshold >
      last.scrollTop
  ) {
    return true;
  }

  if (
    first &&
    state.currentSlice.start > 0 &&
    scrollable.scrollTop - state.threshold < first.scrollTop
  ) {
    return true;
  }

  return false;
};

export const useVirtualizedScrollHandler = (
  scrollableRef: React.RefObject<any> | React.MutableRefObject<any>,
  containerRef: React.RefObject<any> | React.MutableRefObject<any>,
  state: VirtualizedState,
  direction: 'vertical' | 'horizontal',
): React.UIEventHandler => {
  const onScroll = useCallback(
    (e: React.UIEvent): void => {
      const scrollable = scrollableRef.current;
      const container = containerRef.current;

      // If there's no current for either element, then stop here. We can't
      // continue. (This shouldn't ever happen.)
      if (!scrollable || !container) {
        return;
      }

      const virtualizedNodes = Array.from<HTMLElement>(
        container.children as any,
      );

      const measurements = measureVirtualizedNodes(
        container,
        virtualizedNodes,
        direction,
      );

      if (shouldUpdateSlice(state, direction, scrollable, measurements)) {
        const slice = computeVerticalSlice(scrollable, state);
        state.setCurrentSlice(slice);
      }
    },
    [state.currentSlice, state.virtualizedChildren],
  );

  return onScroll;
};
